/**
 * TryStyleClient — the single entry point for the SDK.
 *
 * Usage (browser, clientId flow):
 *   const client = new TryStyleClient({ clientId: 'cred_abc123' });
 *   await client.init(); // fetches platform token from your domain
 *
 * Usage (pre-generated token flow):
 *   const client = new TryStyleClient({ platformToken: 'f47ac10b...' });
 *
 * Usage (manual token generation):
 *   const { platformToken } = await TryStyleClient.generatePlatformToken({
 *     apiUrl: 'https://adminbe.trystyle.fashion',
 *     clientId: 'cred_abc123',
 *     clientSecret: 'secret_xyz',  // only call this server-side!
 *   });
 *   const client = new TryStyleClient({ platformToken });
 */

import type { TryStyleConfig, TryStyleEvents } from './types';
import { EventEmitter } from './utils/emitter';
import { createFetcher } from './utils/fetcher';
import { storage, STORAGE_KEYS } from './utils/storage';
import { PlatformModule } from './platform/platform.module';
import { AuthModule } from './auth/auth.module';
import { DeviceContext } from './utils/deviceContext';
import { AvatarsModule } from './avatars/avatars.module';
import { TryOnModule } from './tryon/tryon.module';
import { ComboModule } from './tryon/combo.module';
import { MetricsModule } from './metrics/metrics.module';

const DEFAULT_API_URL = 'https://adminbe.trystyle.fashion';

export class TryStyleClient {
  readonly platform: PlatformModule;
  readonly auth: AuthModule;
  readonly avatars: AvatarsModule;
  readonly tryon: TryOnModule;
  readonly combo: ComboModule;
  readonly metrics: MetricsModule;
  readonly device: DeviceContext;

  private readonly emitter: EventEmitter<TryStyleEvents>;
  readonly apiUrl: string;
  private _initialized = false;

  /** @internal — Development mock video flag */
  readonly __devMockVideo: boolean;

  constructor(config: TryStyleConfig) {
    this.apiUrl = config.apiUrl ?? DEFAULT_API_URL;
    this.__devMockVideo = !!config.__devMockVideo;
    this.emitter = new EventEmitter<TryStyleEvents>();
    this.device = new DeviceContext(config.device);

    // Platform module — manages platform token
    this.platform = new PlatformModule({
      apiUrl: this.apiUrl,
      clientId: config.clientId,
      initialPlatformToken: config.platformToken,
      usePKCE: config.usePKCE,
      platformType: config.platformType,
      platformIdentifier: config.platformIdentifier,
    });

    // Fetcher — shared typed HTTP client
    const fetcher = createFetcher({
      apiUrl: this.apiUrl,
      getPlatformToken: this.platform.createGetter(),
      getAccessToken: () => storage.get(STORAGE_KEYS.ACCESS_TOKEN),
      getDeviceHeaders: () => this.device.getHeaders(),
      onRefreshToken: async () => {
        // Lazily access auth module to avoid circular reference
        return this.auth.refreshToken();
      },
      onRefreshPlatformToken: async () => {
        try {
          return await this.platform.refreshToken();
        } catch {
          return null;
        }
      },
      onUnauthenticated: () => {
        storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
        storage.remove(STORAGE_KEYS.REFRESH_TOKEN);
        this.emitter.emit('auth:logout', null);
      },
    });

    this.auth = new AuthModule(fetcher, this.emitter);
    this.avatars = new AvatarsModule(fetcher, this.emitter);
    this.metrics = new MetricsModule(fetcher);
    this.combo = new ComboModule(fetcher, this.emitter);
    this.tryon = new TryOnModule(
      fetcher,
      this.emitter,
      config.pollIntervalMs ?? 3000,
      config.pollMaxAttempts ?? 60,
    );
  }

  /**
   * Initialise the SDK.
   * - If using clientId mode: fetches a platform token from the backend.
   * - If using platformToken mode: validates the token is present.
   *
   * Call this once on page load before any other SDK methods.
   */
  async init(): Promise<this> {
    if (this._initialized) return this;
    await this.platform.getToken(); // warms up the token cache

    // Refresh user profile on every page load so stale localStorage is overwritten
    if (this.auth.isAuthenticated()) {
      try {
        await this.auth.getProfile();
      } catch {
        // Network issue — stale cached user info is better than none
      }
    }

    this._initialized = true;
    return this;
  }

  // ─── Event subscription ───────────────────────────────────────────────────

  on<K extends keyof TryStyleEvents>(
    event: K,
    listener: (payload: TryStyleEvents[K]) => void,
  ): () => void {
    return this.emitter.on(event, listener);
  }

  off<K extends keyof TryStyleEvents>(
    event: K,
    listener: (payload: TryStyleEvents[K]) => void,
  ): void {
    this.emitter.off(event, listener);
  }

  // ─── Static helpers ───────────────────────────────────────────────────────

  /**
   * Generate a platform token using clientId + clientSecret.
   *
   * ⚠️  SECURITY: This method must only be called SERVER-SIDE (Node.js, PHP, Python, etc.).
   * Never expose your clientSecret in browser/client-side code.
   *
   * @example
   *   // In your Express/Next.js API route:
   *   const token = await TryStyleClient.generatePlatformToken({
   *     clientId: process.env.TRYSTYLE_CLIENT_ID,
   *     clientSecret: process.env.TRYSTYLE_CLIENT_SECRET,
   *   });
   *   // Then inject `token.platformToken` into your HTML/page props
   */
  static async generatePlatformToken(opts: {
    clientId: string;
    clientSecret: string;
    apiUrl?: string;
  }): Promise<{ platformToken: string; expiresAt: string; expiresIn: number }> {
    const url = opts.apiUrl ?? DEFAULT_API_URL;

    const res = await fetch(`${url}/api/v1/public/platform/token`, {
      method: 'POST',
      headers: {
        'X-Client-Id': opts.clientId,
        'X-Client-Secret': opts.clientSecret,
      },
    });

    if (!res.ok) {
      let msg = `Failed to generate platform token: ${res.status}`;
      try {
        const body = await res.json() as { message?: string };
        if (body.message) msg = body.message;
      } catch { /* ignore */ }
      throw new Error(msg);
    }

    const json = await res.json() as {
      success: boolean;
      data: { platformToken: string; expiresAt: string; expiresIn: number };
    };

    return json.data;
  }
}
